import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  IconButton,
  Link,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CloudUpload, Delete, CheckCircle } from "@mui/icons-material";
import { useState, useEffect } from "react";
import apiClient from "../../../context/apiClient";

const DocsLinksStep = ({
  profileData,
  handleInputChange,
  currentLink,
  setCurrentLink,
  initialLinkState,
  handleAddItem,
  handleDeleteItem,
  showNotification,
}) => {
  const theme = useTheme();
  const [uploadingStatus, setUploadingStatus] = useState({});
  const [otherDocDescription, setOtherDocDescription] = useState("");
  const [, setOtherDocFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // CHANGED: Added loading state for initial fetch

  // CHANGED: Fetch documents on component mount
  useEffect(() => {
    const fetchDocuments = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get("/resume-documents/");
        if (response.data && Array.isArray(response.data)) {
          const formattedDocuments = response.data.map((doc) => ({
            id: doc.id,
            url: doc.document_url,
            type: doc.document_type,
            description: doc.description || `Document ${doc.id}`,
          }));
          handleInputChange("uploaded_documents", formattedDocuments);
        }
      } catch (error) {
        console.error("Failed to fetch documents:", error);
        showNotification("Could not load your saved documents.", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this runs only once on mount

  const documentTypes = [
    { value: "TENTH_CERTIFICATE", label: "10th Certificate", compulsory: true },
    {
      value: "TWELFTH_CERTIFICATE",
      label: "12th Certificate",
      compulsory: false,
    },
    { value: "DIPLOMA", label: "Diploma Certificate", compulsory: false },
    { value: "DEGREE", label: "Degree Certificate", compulsory: true },
    {
      value: "POST_GRADUATION",
      label: "Post Graduation Certificate",
      compulsory: false,
    },
    { value: "INTERNSHIP", label: "Internship Certificate", compulsory: false },
    { value: "SKILL", label: "Skill Certificate", compulsory: false },
  ];

  const handleDocumentUpload = async (file, documentType, description = "") => {
    if (!file) return;

    if (!file.type.match(/image\/(jpeg|jpg|png)|application\/pdf/)) {
      showNotification(
        "Please upload only JPG, JPEG, PNG, or PDF files.",
        "error"
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showNotification("File size should be less than 5MB.", "error");
      return;
    }

    const loadingKey =
      documentType === "OTHER" ? `OTHER_${description}` : documentType;
    setUploadingStatus((prev) => ({ ...prev, [loadingKey]: true }));

    const formData = new FormData();
    formData.append("document_file", file);
    formData.append("document_type", documentType);
    if (description) {
      formData.append("description", description);
    }

    try {
      // NOTE: API endpoint changed to match GET request for consistency
      const response = await apiClient.post("/resume-documents/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data && response.data.document_url) {
        const newDocument = {
          id: response.data.id,
          url: response.data.document_url,
          type: response.data.document_type,
          description: response.data.description || file.name,
        };

        const updatedDocuments = [
          ...(profileData.uploaded_documents || []),
          newDocument,
        ];
        handleInputChange("uploaded_documents", updatedDocuments);

        const docLabel =
          documentTypes.find((d) => d.value === documentType)?.label ||
          "Document";
        showNotification(`${docLabel} uploaded successfully!`, "success");

        if (documentType === "OTHER") {
          setOtherDocDescription("");
          setOtherDocFile(null);
        }
      } else {
        throw new Error("API did not return a document URL.");
      }
    } catch (error) {
      console.error("Document upload error:", error);
      showNotification(`Failed to upload document. Please try again.`, "error");
    } finally {
      setUploadingStatus((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleDocumentDelete = async (docId, docType) => {
    const loadingKey = docId;
    setUploadingStatus((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      // NOTE: API endpoint changed to match GET request for consistency
      await apiClient.delete(`/resume-documents/${docId}/`);
      const updatedDocuments = profileData.uploaded_documents.filter(
        (doc) => doc.id !== docId
      );
      handleInputChange("uploaded_documents", updatedDocuments);
      showNotification("Document deleted successfully!", "success");
    } catch (error) {
      console.error("Document deletion error:", error);
      showNotification("Failed to delete document. Please try again.", "error");
    } finally {
      setUploadingStatus((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const hasCompulsoryCertificates = () => {
    const uploadedTypes =
      profileData.uploaded_documents?.map((doc) => doc.type) || [];
    const hasSSC = uploadedTypes.includes("TENTH_CERTIFICATE");
    const hasGraduation = uploadedTypes.includes("DEGREE");
    const hasHSCorDiploma =
      uploadedTypes.includes("TWELFTH_CERTIFICATE") ||
      uploadedTypes.includes("DIPLOMA");
    return hasSSC && hasGraduation && hasHSCorDiploma;
  };

  const cardStyle = {
    bgcolor: "background.paper",
    p: 3,
    borderRadius: "24px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
  };


  const titleStyle = { color: "primary.main", fontWeight: "bold", mb: 1 };

  const fileNameStyle = {
    mt: 1,
    p: 2,
    bgcolor: theme => alpha(theme.palette.primary.main, 0.02),
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  };


  const renderFileUpload = (docInfo) => {
    // Note: .find() will only show the first document if multiple of the same type exist.
    // This matches the original logic.
    const uploadedDoc = profileData.uploaded_documents?.find(
      (d) => d.type === docInfo.value
    );
    const isUploading = uploadingStatus[docInfo.value];
    const isDeleting = uploadedDoc ? uploadingStatus[uploadedDoc.id] : false;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: "primary.main" }}>
          {docInfo.label}{" "}
          {docInfo.compulsory && (
            <span style={{ color: theme.palette.error.main }}>*</span>
          )}
        </Typography>
        {!uploadedDoc ? (
          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={
              isUploading ? <CircularProgress size={20} /> : <CloudUpload />
            }
            disabled={isUploading}
            sx={{
              height: "56px",
              color: "text.secondary",
              textTransform: "none",
              justifyContent: "flex-start",
              px: 2,
              borderRadius: "12px",
              border: theme => `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
              bgcolor: theme => alpha(theme.palette.primary.main, 0.02),
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
              },
            }}

          >
            {isUploading ? `Uploading...` : `Upload ${docInfo.label}`}
            <input
              type="file"
              hidden
              accept="image/jpeg,image/jpg,image/png,application/pdf"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  handleDocumentUpload(e.target.files[0], docInfo.value);
                }
              }}
            />
          </Button>
        ) : (
          <Box sx={fileNameStyle}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                overflow: "hidden",
              }}
            >
              <CheckCircle
                color="success"
                sx={{ fontSize: 20, flexShrink: 0 }}
              />
              <Typography variant="body2" noWrap>
                {docInfo.label} Uploaded
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {isDeleting ? (
                <CircularProgress size={20} sx={{ mr: 1 }} />
              ) : (
                <>
                  <Button
                    size="small"
                    onClick={() => window.open(uploadedDoc.url, "_blank")}
                    color="primary"
                    sx={{ textTransform: "none" }}
                  >
                    Preview
                  </Button>
                  <IconButton
                    onClick={() =>
                      handleDocumentDelete(uploadedDoc.id, uploadedDoc.type)
                    }
                    size="small"
                    color="primary"
                  >
                    <Delete />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {!hasCompulsoryCertificates() &&
        !isLoading && ( // CHANGED: Don't show alert while loading
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Certificate Upload Requirements:</strong>
              <br />• 10th (SSC) and Graduation certificates are compulsory.
              <br />• Either 12th (HSC) or Diploma certificate is required.
            </Typography>
          </Alert>
        )}

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Paper sx={cardStyle}>
            <Typography variant="h6" sx={titleStyle}>
              Upload Documents
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload your educational and professional certificates. Files
              should be in JPG, JPEG, PNG, or PDF format (max 5MB).
            </Typography>

            {/* CHANGED: Conditional rendering for loading state */}
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "200px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {documentTypes.map((docInfo) => (
                  <Grid item xs={12} md={6} key={docInfo.value}>
                    {renderFileUpload(docInfo)}
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, color: "primary.main" }}
                  >
                    Other Documents
                  </Typography>
                  {profileData.uploaded_documents
                    ?.filter((d) => d.type === "OTHER")
                    .map((doc) => {
                      const isDeleting = uploadingStatus[doc.id];
                      return (
                        <Box key={doc.id} sx={fileNameStyle}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              overflow: "hidden",
                            }}
                          >
                            <CheckCircle
                              color="success"
                              sx={{ fontSize: 20, flexShrink: 0 }}
                            />
                            <Typography
                              variant="body2"
                              noWrap
                              title={doc.description}
                            >
                              {doc.description}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            {isDeleting ? (
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                            ) : (
                              <>
                                <Button
                                  size="small"
                                  onClick={() => window.open(doc.url, "_blank")}
                                  color="primary"
                                  sx={{ textTransform: "none" }}
                                >
                                  Preview
                                </Button>
                                <IconButton
                                  onClick={() =>
                                    handleDocumentDelete(doc.id, doc.type)
                                  }
                                  size="small"
                                  color="primary"
                                >
                                  <Delete />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  <TextField
                    fullWidth
                    size="small"
                    label="Description for new document"
                    value={otherDocDescription}
                    onChange={(e) => setOtherDocDescription(e.target.value)}
                    sx={{ mt: 2 }}
                  />
                  <Button
                    variant="contained"
                    component="label"
                    fullWidth
                    startIcon={
                      uploadingStatus[`OTHER_${otherDocDescription}`] ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <CloudUpload />
                      )
                    }
                    disabled={
                      uploadingStatus[`OTHER_${otherDocDescription}`] ||
                      !otherDocDescription
                    }
                    color="primary"
                    sx={{
                      mt: 2,
                      borderRadius: "12px",
                      height: "56px",
                      fontWeight: "bold",
                      boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                    }}
                  >
                    {uploadingStatus[`OTHER_${otherDocDescription}`]
                      ? "Uploading..."
                      : "Select and Upload Other Document"}
                    <input
                      type="file"
                      hidden
                      accept="image/jpeg,image/jpg,image/png,application/pdf"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleDocumentUpload(
                            e.target.files[0],
                            "OTHER",
                            otherDocDescription
                          );
                        }
                      }}
                    />
                  </Button>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={4} sx={{ height: "100%" }}>
            <Paper sx={{ ...cardStyle, flexGrow: 1 }}>
              <Typography variant="h6" sx={titleStyle}>
                Professional Links
              </Typography>
              <Stack spacing={2} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    fullWidth
                    label="Platform"
                    value={currentLink.platform}
                    onChange={(e) =>
                      setCurrentLink({
                        ...currentLink,
                        platform: e.target.value,
                      })
                    }
                  />
                  <TextField
                    fullWidth
                    label="Link URL"
                    value={currentLink.link}
                    onChange={(e) =>
                      setCurrentLink({ ...currentLink, link: e.target.value })
                    }
                  />
                </Stack>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (currentLink.platform && currentLink.link) {
                      handleAddItem("professional_links", currentLink);
                      setCurrentLink({ platform: "", link: "" });
                    }
                  }}
                  color="primary"
                  sx={{
                    width: "100%",
                    borderRadius: "12px",
                    height: "56px",
                    fontWeight: "bold",
                    boxShadow: theme => `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }}
                  disabled={!currentLink.platform || !currentLink.link}
                >
                  Add Professional Link
                </Button>

              </Stack>
              <Stack spacing={1} sx={{ mt: 2, flexGrow: 1, overflowY: "auto" }}>
                {profileData.professional_links?.map((link, index) => (
                  <Paper key={index} sx={fileNameStyle}>
                    <Box
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography variant="body1" fontWeight="bold">
                        {link.platform}
                      </Typography>
                      <Link
                        href={link.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        noWrap
                        color="secondary"
                      >
                        {link.link}
                      </Link>
                    </Box>
                    <IconButton
                      onClick={() =>
                        handleDeleteItem("professional_links", index)
                      }
                      size="small"
                      color="primary"
                    >
                      <Delete />
                    </IconButton>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DocsLinksStep;
